import { createLogger } from "../utils/logger.js";
//#region ../src/sensors/gps.ts
/**
* GPS Module
*
* Handles Geolocation API access and device orientation/compass.
*/
const log = createLogger("GPS");
let watchId = null;
let orientationHandler = null;
/**
* Start watching GPS position.
* Idempotent: clears any existing watch before starting a new one
* (Issue 4, 2026-02-27 user feedback — prevents watch leaks when
* transitioning from warm-up to recording watch).
*/
function startGpsWatch(onPosition, onError) {
	if (!navigator.geolocation) {
		log.error("Geolocation API not available");
		return;
	}
	stopGpsWatch();
	watchId = navigator.geolocation.watchPosition((pos) => {
		onPosition({
			lat: pos.coords.latitude,
			lon: pos.coords.longitude,
			altitude: pos.coords.altitude,
			accuracy: pos.coords.accuracy,
			altitudeAccuracy: pos.coords.altitudeAccuracy,
			heading: pos.coords.heading,
			speed: pos.coords.speed,
			timestamp: pos.timestamp
		});
	}, (err) => {
		log.error("Error:", err.message);
		onError?.(err);
	}, {
		enableHighAccuracy: true,
		maximumAge: 0,
		timeout: 1e4
	});
	log.info("Watch started");
}
/**
* Stop watching GPS position
*/
function stopGpsWatch() {
	if (watchId !== null) {
		navigator.geolocation.clearWatch(watchId);
		watchId = null;
		log.info("Watch stopped");
	}
}
/**
* Start listening for device orientation (compass)
*/
function startOrientationWatch(onOrientation) {
	stopOrientationWatch();
	orientationHandler = (event) => {
		onOrientation({
			alpha: event.alpha,
			beta: event.beta,
			gamma: event.gamma,
			absolute: event.absolute
		});
	};
	window.addEventListener("deviceorientation", orientationHandler);
	log.info("Orientation watch started");
}
/**
* Stop listening for device orientation
*/
function stopOrientationWatch() {
	if (orientationHandler) {
		window.removeEventListener("deviceorientation", orientationHandler);
		orientationHandler = null;
		log.info("Orientation watch stopped");
	}
}
/**
* Request permission for device orientation (required on iOS 13+)
*/
async function requestOrientationPermission() {
	const DeviceOrientationEventWithPermission = DeviceOrientationEvent;
	if (typeof DeviceOrientationEventWithPermission.requestPermission === "function") try {
		return await DeviceOrientationEventWithPermission.requestPermission() === "granted";
	} catch {
		return false;
	}
	return true;
}
//#endregion
export { requestOrientationPermission, startGpsWatch, startOrientationWatch, stopGpsWatch, stopOrientationWatch };
